// @ts-strict-ignore
import * as contentful from "contentful";
import { isProductionEnv, isPreviewBranch } from "../config/utils";
import {
  CONTENTFUL_SPACE_ID,
  CONTENTFUL_CRYPTOPEDIA_SPACE_ID
} from "../constants/api-constants";
import { routing } from "../i18n/routing";
import { DynamicApis } from "./api/dynamic";
import { logger } from "observability";
import {
  determineRoutePriority,
  determineContentPriority
} from "../utils/sitemap-priorities";

const usePreview = !isProductionEnv || isPreviewBranch;
const host = usePreview ? "preview.contentful.com" : "cdn.contentful.com";

// Primary Contentful Space
export const accessToken = process.env.NEXT_PUBLIC_CONTENTFUL_DELIVERY_TOKEN;
export const contentfulClient = contentful.createClient({
  accessToken,
  space: CONTENTFUL_SPACE_ID,
  host
});

// Cryptopedia Contentful Space
export const accessTokenCryptopedia =
  process.env.NEXT_PUBLIC_CONTENTFUL_CRYPTOPEDIA_TOKEN;
export const contentfulClientCryptopedia = contentful.createClient({
  accessToken: accessTokenCryptopedia,
  space: CONTENTFUL_CRYPTOPEDIA_SPACE_ID,
  host
});

const CONTENTFUL_REQUEST_LIMIT_MAX = 1000;

const fetchAllEntries = async (
  client: contentful.ContentfulClientApi,
  params: any
) => {
  const limit = CONTENTFUL_REQUEST_LIMIT_MAX;
  let skip = 0;
  const allItems: any[] = [];

  while (true) {
    const result = await client.getEntries({
      ...params,
      limit,
      skip
    });

    allItems.push(...result.items);

    if (result.items.length < limit) {
      break;
    }
    skip += limit;
  }

  return {
    items: allItems,
    total: allItems.length
  };
};

// Content route structure for reusable route generation
export interface ContentfulRoute {
  path: string;
  lastModified?: Date;
  changeFrequency?:
    | "always"
    | "hourly"
    | "daily"
    | "weekly"
    | "monthly"
    | "yearly"
    | "never";
  priority?: number;
}

// Helper function to safely convert timestamps to Date objects
const safeDate = (timestamp?: string | number | Date): Date => {
  if (!timestamp) return new Date();
  const date = new Date(timestamp);
  return isNaN(date.getTime()) ? new Date() : date;
};

// Get all Contentful routes for SEO/sitemap purposes
export const fetchContentfulRoutes = async (): Promise<ContentfulRoute[]> => {
  const routes: ContentfulRoute[] = [];
  const defaultLocale = routing.defaultLocale;

  try {
    // Fetch all content types in parallel
    const [
      pageTemplates,
      blogPosts,
      legalPages,
      feesPages,
      coinDetails,
      areasOfAvailability,
      cryptopediaPosts,
      cryptopediaAuthors,
      cryptopediaStories,
      cryptopediaTopics
    ] = await Promise.all([
      DynamicApis.Contentful.fetchAllPageTemplates(defaultLocale),
      fetchAllEntries(contentfulClient, {
        content_type: "blogPost",
        select: ["fields.slug"]
      }),
      fetchAllEntries(contentfulClient, {
        content_type: "legalPage",
        select: ["fields.slug", "fields.artemis"]
      }),
      fetchAllEntries(contentfulClient, {
        content_type: "feesPage",
        select: ["fields.slug"]
      }),
      fetchAllEntries(contentfulClient, {
        content_type: "coinDetail",
        select: ["fields.slug", "fields.hideFromPricesPage", "fields.isTrading"]
      }),
      fetchAllEntries(contentfulClient, {
        content_type: "areaOfAvailability",
        select: ["fields.slug"]
      }),
      fetchAllEntries(contentfulClientCryptopedia, {
        content_type: "cryptopediaPost",
        select: ["fields.slug"]
      }),
      fetchAllEntries(contentfulClientCryptopedia, {
        content_type: "cryptopediaAuthor",
        select: ["fields.slug"]
      }),
      fetchAllEntries(contentfulClientCryptopedia, {
        content_type: "cryptopediaStory",
        select: ["fields.slug"]
      }),
      fetchAllEntries(contentfulClientCryptopedia, {
        content_type: "cryptopediaTopic",
        select: ["fields.slug"]
      })
    ]);

    // Add dynamic template pages (excluding homepage)
    pageTemplates?.items?.forEach((item) => {
      if (item.fields?.isMigrated && item.fields?.slug !== "homepage") {
        const priority = determineRoutePriority(item.fields.slug);

        routes.push({
          path: `/${item.fields.slug}`,
          lastModified: safeDate(item.sys?.updatedAt),
          changeFrequency: "monthly",
          priority
        });
      }
    });

    // Add blog posts
    blogPosts.items.forEach((item: any) => {
      if (item.fields?.slug) {
        routes.push({
          path: `/blog/${item.fields.slug}`,
          lastModified: safeDate(item.sys?.updatedAt),
          changeFrequency: "monthly",
          priority: determineContentPriority(
            "blog",
            `/blog/${item.fields.slug}`
          )
        });
      }
    });

    // Add legal pages (both regular and artemis)
    legalPages.items.forEach((item: any) => {
      if (item.fields?.slug) {
        const path = item.fields.artemis
          ? `/artemis/legal/${item.fields.slug}`
          : `/legal/${item.fields.slug}`;
        routes.push({
          path,
          lastModified: safeDate(item.sys?.updatedAt),
          changeFrequency: "monthly",
          priority: determineContentPriority("legal", path)
        });
      }
    });

    // Add fees pages
    feesPages.items.forEach((item: any) => {
      if (item.fields?.slug) {
        const path = `/fees/${item.fields.slug}`;
        routes.push({
          path,
          lastModified: safeDate(item.sys?.updatedAt),
          changeFrequency: "monthly",
          priority: determineContentPriority("fees", path)
        });
      }
    });

    // Add coin detail pages (prices and how-to-buy)
    coinDetails.items.forEach((item: any) => {
      if (item.fields?.slug) {
        // Add to prices if not hidden
        if (!item.fields.hideFromPricesPage) {
          const pricesPath = `/prices/${item.fields.slug}`;
          routes.push({
            path: pricesPath,
            lastModified: safeDate(item.sys?.updatedAt),
            changeFrequency: "daily",
            priority: determineContentPriority("prices", pricesPath)
          });
        }

        // Add to how-to-buy if trading enabled
        if (item.fields.isTrading) {
          const howToBuyPath = `/how-to-buy/${item.fields.slug}`;
          routes.push({
            path: howToBuyPath,
            lastModified: safeDate(item.sys?.updatedAt),
            changeFrequency: "weekly",
            priority: determineContentPriority("how-to-buy", howToBuyPath)
          });
        }
      }
    });

    // Add areas of availability
    areasOfAvailability.items.forEach((item: any) => {
      if (item.fields?.slug) {
        const path = `/areas-of-availability/${item.fields.slug}`;
        routes.push({
          path,
          lastModified: safeDate(item.sys?.updatedAt),
          changeFrequency: "monthly",
          priority: determineContentPriority("areas-of-availability", path)
        });
      }
    });

    // Add cryptopedia content
    cryptopediaPosts.items.forEach((item: any) => {
      if (item.fields?.slug) {
        const path = `/cryptopedia/${item.fields.slug}`;
        routes.push({
          path,
          lastModified: safeDate(item.sys?.updatedAt),
          changeFrequency: "monthly",
          priority: determineContentPriority("cryptopedia", path)
        });
      }
    });

    cryptopediaAuthors.items.forEach((item: any) => {
      if (item.fields?.slug) {
        const path = `/cryptopedia/author/${item.fields.slug}`;
        routes.push({
          path,
          lastModified: safeDate(item.sys?.updatedAt),
          changeFrequency: "monthly",
          priority: determineContentPriority("cryptopedia-author", path)
        });
      }
    });

    cryptopediaStories.items.forEach((item: any) => {
      if (item.fields?.slug) {
        const path = `/cryptopedia/story/${item.fields.slug}`;
        routes.push({
          path,
          lastModified: safeDate(item.sys?.updatedAt),
          changeFrequency: "monthly",
          priority: determineContentPriority("cryptopedia-story", path)
        });
      }
    });

    cryptopediaTopics.items.forEach((item: any) => {
      if (item.fields?.slug) {
        const path = `/cryptopedia/topic/${item.fields.slug}`;
        routes.push({
          path,
          lastModified: safeDate(item.sys?.updatedAt),
          changeFrequency: "monthly",
          priority: determineContentPriority("cryptopedia-topic", path)
        });
      }
    });
  } catch (error) {
    logger.error("Error fetching Contentful routes:", error as Error);
  }

  return routes;
};
